import { DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Inject, Injectable, Optional, PLATFORM_ID, inject } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { filter, flatMap, tap } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { SearchResultUiService } from '../../pages/search-result/services/search-result-ui.service';
import { HotelSummaryApiModel, SearchResponse } from '@core/models/domains/hotel';
import { API_IHO_URL } from '../../app.module';

@Injectable()
export class MetaScriptInterceptor implements HttpInterceptor {
  router = inject(Router);
  private readonly siteOrigin = 'https://www.iranhotelonline.com';
  private readonly faqScriptId = 'json-ld-faq';
  private readonly infoScriptId = 'json-ld-info';
  private readonly infoItemsScriptId = 'json-ld-info-items';
  private readonly breadcrumbScriptId = 'json-ld-breadcrumb';
  private faqUpdatedDuringNavigation = false;
  private hotelSummaryJsonLdActive = false;
  isShab: boolean = false;
  isMashhad: boolean = false;
  constructor(
    private route: ActivatedRoute,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Optional() @Inject(API_IHO_URL) private baseUrl?: string
  ) {
    if (isPlatformBrowser(this.platformId)) {
      let hasCompletedInitialNavigation = this.router.navigated;

      this.router.events.subscribe((event) => {
        if (event instanceof NavigationStart && hasCompletedInitialNavigation) {
          this.faqUpdatedDuringNavigation = false;
          this.hotelSummaryJsonLdActive = false;
          this.removePageJsonLdScripts();
        }

        if (event instanceof NavigationEnd) {
          if (hasCompletedInitialNavigation && !this.faqUpdatedDuringNavigation) {
            this.removeScript(this.faqScriptId);
          }

          hasCompletedInitialNavigation = true;
        }
      });
    }
  }


  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // فقط اگر درخواست به API مشخصی است
    if (
      req.url.includes('search/filter') ||
      req.url.includes('accomodation/filter') ||
      req.url.includes('hotelInfo/getHotelSummaryInfo') ||
      req.url.includes('accomodation/getHotelSummaryInfo') ||
      req.url.includes('home/GetAllSections') ||
      req.url.includes('accomodation/GetAllSections') ||
      req.url.includes('dynamicSearchFilter') ||
      req.url.includes('international-hotels')  ||
      req.url.includes('citypage/district') ||
      req.url.includes('GetChainHotels') ||
      req.url.includes('chainhotel/GetHotels') ||
      req.url.includes('reviews/ReviewsOfCity') ||
      req.url.includes('iranhotels')

    ) {
      return next.handle(req).pipe(
        tap((event: HttpEvent<any>) => {
          if (event instanceof HttpResponse) {
            if (req.url.includes('home/GetAllSections/5')){
              this.isMashhad = true;
            }else{
              this.isMashhad = false;
            }
            let regexMap = /^\/ecolodge\/.+?(\/map\/?$|\?|$)/;
            let isMapEcolodge: any = this.router.url.search(regexMap);
            if (isMapEcolodge > -1) {
              this.isShab = true;
            }
            this.handleResponse(event, req.url);
            this.createCanonical(req.url, next, req, event);
            if (
              req.url.includes('/search/filter?') && // only for plp page (Search Result)
              isPlatformServer(this.platformId) // we need it just for first render
            ) {
              const paramsArray = this.document.location.search.split('&') ?? '';
              this.createNextAndPrevLinks(paramsArray, paramsArray?.length > 1, event.body);
            } else if (isPlatformBrowser(this.platformId)) {
              this.hidePaginationButtons();
            }
          }
        })
      );
    }

    return next.handle(req);
  }

  private handleResponse(event: HttpResponse<any>, requestUrl: string) {
    const responseBody = event.body;

    if (!responseBody) return;

    const isHotelSummary =
      requestUrl.includes('hotelInfo/getHotelSummaryInfo') ||
      requestUrl.includes('accomodation/getHotelSummaryInfo');

    if (isHotelSummary) {
      this.addHotelSummaryJsonLd(responseBody);
    } else if (!this.hotelSummaryJsonLdActive) {
      if (responseBody.JsonBreadcrumb) {
        this.addScripts(responseBody.JsonBreadcrumb, this.breadcrumbScriptId);
      }
      if (Object.prototype.hasOwnProperty.call(responseBody, 'JsonFaq')) {
        this.faqUpdatedDuringNavigation = true;

        if (responseBody.JsonFaq) {
          this.addScripts(responseBody.JsonFaq, this.faqScriptId);
        } else {
          this.removeScript(this.faqScriptId);
        }
      }
      if (responseBody.JsonInfo) {
        this.addScripts(responseBody.JsonInfo, this.infoScriptId);
      }
    }
    if (responseBody.JsonInfoItems && !this.hotelSummaryJsonLdActive) {
      this.addScripts(responseBody.JsonInfoItems, this.infoItemsScriptId);
    }
    if (responseBody.MetaTags) {
      this.addMetaTags(responseBody.MetaTags, event.url);
    }
    if (responseBody.MetaData) {
      this.cityAddMetaTags(responseBody.MetaData, event.url);
    }

  }

  private addHotelSummaryJsonLd(doc: HotelSummaryApiModel) {
    this.hotelSummaryJsonLdActive = true;
    this.removePageJsonLdScripts();
    this.addJsonLdScript(this.buildJsonInfo(doc), this.infoScriptId);

    const jsonBreadcrumb = this.buildJsonBreadcrumb(doc);
    if (jsonBreadcrumb) {
      this.addJsonLdScript(jsonBreadcrumb, this.breadcrumbScriptId);
    } else {
      this.removeScript(this.breadcrumbScriptId);
    }

    this.faqUpdatedDuringNavigation = true;
    const jsonFaq = this.buildJsonFaq(doc);
    if (jsonFaq) {
      this.addJsonLdScript(jsonFaq, this.faqScriptId);
    } else {
      this.removeScript(this.faqScriptId);
    }
  }

  private buildJsonInfo(doc: HotelSummaryApiModel) {
    const about = doc.AboutHotel ?? doc.About;
    const header = doc.HotelHeader;
    const star = about?.Star ?? header?.Star;
    const score = about?.Score ?? header?.Score;
    const address = about?.Address ?? header?.Address;
    const pictures = doc.ModalHotelGallery?.Pictures ?? [];
    const breadcrumbs: { Url?: string }[] = doc.BreadCrumb ?? [];
    const lastCrumb = breadcrumbs[breadcrumbs.length - 1];
    const legacyJsonInfo = this.parseJsonLd(doc.JsonInfo);
    const telephone = doc.Telephone ?? this.readString(legacyJsonInfo, 'telephone');
    const image = pictures[0]?.Webp || pictures[0]?.Jpg;
    const name = about?.FullName ?? header?.FullName ?? doc.Name ?? '';
    const description = this.toPlainText(
      about?.AboutProperty || about?.BriefDescription || ''
    );

    return {
      '@context': 'https://schema.org',
      '@type': 'Hotel',
      ...(image ? { image } : {}),
      url: this.toAbsoluteUrl(lastCrumb?.Url),
      ...(description ? { description } : {}),
      ...(star?.GradeId
        ? {
            starRating: {
              '@type': 'Rating',
              ratingValue: star.GradeId,
              bestRating: 5,
            },
          }
        : {}),
      ...(score?.ScoreNumber && score.Total
        ? {
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: score.ScoreNumber,
              bestRating: 5,
              ratingCount: score.Total,
            },
          }
        : {}),
      ...(address?.Address || address?.CityName
        ? {
            address: {
              '@type': 'PostalAddress',
              streetAddress: address.Address ?? '',
              addressLocality: address.CityName ?? '',
              ...(address.State ? { addressRegion: address.State } : {}),
            },
          }
        : {}),
      ...(doc.Policies?.CheckInTime ? { checkinTime: doc.Policies.CheckInTime } : {}),
      ...(doc.Policies?.CheckOutTime ? { checkoutTime: doc.Policies.CheckOutTime } : {}),
      ...(telephone ? { telephone } : {}),
      name,
    };
  }

  private buildJsonBreadcrumb(doc: HotelSummaryApiModel) {
    const breadcrumbs = (doc.BreadCrumb ?? []).filter((item) => item.Name || item.Url);
    if (!breadcrumbs.length) return null;

    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@id': this.toAbsoluteUrl(item.Url),
          name: item.Name ?? '',
        },
      })),
    };
  }

  private buildJsonFaq(doc: HotelSummaryApiModel) {
    const faqs = (doc.FAQs ?? []).filter((faq) => faq.Title && faq.Description);
    if (!faqs.length) return null;

    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.Title ?? '',
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.Description ?? '',
        },
      })),
    };
  }

  private toAbsoluteUrl(path?: string | null): string {
    if (!path) return this.siteOrigin;
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    return this.siteOrigin + (path.startsWith('/') ? path : '/' + path);
  }

  private addJsonLdScript(data: object, id?: string) {
    this.addScripts(JSON.stringify(data).replace(/</g, '\\u003c'), id);
  }

  private parseJsonLd(script?: string): Record<string, unknown> | null {
    if (!script) return null;

    try {
      return JSON.parse(this.removeJsonLdScriptTag(script)) as Record<string, unknown>;
    } catch {
      return null;
    }
  }

  private readString(source: Record<string, unknown> | null, key: string): string | undefined {
    const value = source?.[key];
    return typeof value === 'string' && value ? value : undefined;
  }

  private toPlainText(value: string): string {
    const container = this.document.createElement('div');
    container.innerHTML = value;
    return (container.textContent ?? '').replace(/\s+/g, ' ').trim();
  }

  private removeJsonLdScriptTag(script: string): string {
    return script
      .replace(/<script\b[^>]*type=["']application\/ld\+json["'][^>]*>/i, '')
      .replace(/<\/script>/i, '')
      .trim();
  }

  private addMetaTags(
    metaTags: { Keywords: string; PageDescription: string; Title: string },
    url: any
  ) {
    for (const meta in metaTags) {
      if (Object.prototype.hasOwnProperty.call(metaTags, meta)) {
        const itemValue = (metaTags as any)[meta];
        switch (meta) {
    
          case 'PageDescription': {
            this.createOrGetMetaElement('name', 'description', itemValue);
            this.createOrGetMetaElement('property', 'og:description', itemValue);
            this.createOrGetMetaElement('name', 'twitter:description', itemValue);
            break;
          }

          case 'Title': {
            this.createOrGetMetaElement('property', 'og:title', itemValue);
            this.createOrGetMetaElement('name', 'twitter:title', itemValue);
            // ---
            let titleEl = this.document.querySelector('title');

            if (!titleEl) titleEl = this.document.createElement('title');

            titleEl.textContent = itemValue;

            this.document.head.appendChild(titleEl);
            break;
          }

          default:
            break;
        }
      }
    }


  }
  private cityAddMetaTags(
    metaTags: { CanonicalUrl: string; PageDescription: string, PageKeywords: string, PageTitle: string },
    url: any
  ) {
    for (const meta in metaTags) {
      if (Object.prototype.hasOwnProperty.call(metaTags, meta)) {
        const itemValue = (metaTags as any)[meta];
        switch (meta) {
    
          case 'PageDescription': {
            this.createOrGetMetaElement('name', 'description', itemValue);
            this.createOrGetMetaElement('property', 'og:description', itemValue);
            this.createOrGetMetaElement('name', 'twitter:description', itemValue);
            break;
          }

          case 'PageTitle': {
            this.createOrGetMetaElement('property', 'og:title', itemValue);
            this.createOrGetMetaElement('name', 'twitter:title', itemValue);
            // ---
            let titleEl = this.document.querySelector('title');

            if (!titleEl) titleEl = this.document.createElement('title');

            titleEl.textContent = itemValue;

            this.document.head.appendChild(titleEl);
            break;
          }

          case 'CanonicalUrl': {
            let linkCanonical = this.document.querySelector('link[rel="canonical"]');
            if (!linkCanonical) linkCanonical = this.document.createElement('link');
            linkCanonical.setAttribute('rel', 'canonical');
            linkCanonical.setAttribute('href',decodeURIComponent(itemValue).toLocaleLowerCase());
            break
          }

          case 'PageKeywords': {
            this.createOrGetMetaElement('name', 'keywords', itemValue);
            // ---
            break;
          }

          default:
            break;
        }
      }
    }


  }

  private createOrGetMetaElement(attributeKey: string, attributeValue: string, content: string) {
    let metaEl = this.document.querySelector(`meta[${attributeKey}="${attributeValue}"]`);
    if (!metaEl) metaEl = this.document.createElement('meta');

    metaEl.setAttribute(attributeKey, attributeValue);
    metaEl.setAttribute('content', content);
    this.document.head.insertBefore(metaEl, this.document.head.firstElementChild);
    //this.document.head.appendChild(metaEl)
  }

  private addScripts(script: string, id?: string) {
    // اضافه کردن اسکریپتها
    const scriptEl = this.document.createElement('script');
    scriptEl.type = 'application/ld+json';
    const scriptWithoutTag = this.removeJsonLdScriptTag(script);

    scriptEl.text = scriptWithoutTag;

    if (id) {
      scriptEl.id = id;
      const existingScript = this.document.getElementById(id);

      if (existingScript?.parentNode) {
        existingScript.parentNode.replaceChild(scriptEl, existingScript);
        return;
      }
    }

    this.document.body.appendChild(scriptEl);
  }

  private removeScript(id: string) {
    const script = this.document.getElementById(id);
    script?.parentNode?.removeChild(script);
  }

  private removePageJsonLdScripts() {
    const scripts = this.document.body?.querySelectorAll('script[type="application/ld+json"]');

    Array.from(scripts ?? []).forEach((script) => {
      script.parentNode?.removeChild(script);
    });
  }

  setCanonical(flag: boolean) {
    let queryParams = this.route.snapshot.queryParams;

    let metaEl = this.document.querySelector(`meta[name="robots"]`);
    if (!metaEl) metaEl = this.document.createElement('meta');

    let linkCanonical = this.document.querySelector('link[rel="canonical"]');
    if (!linkCanonical) linkCanonical = this.document.createElement('link');
    linkCanonical.setAttribute('rel', 'canonical');

    if (this.document.location.search != '') {
      metaEl.setAttribute('name', 'robots');
      // TODO what is this ?
      if (
        !flag &&
        (queryParams?.['page'] || queryParams['page']?.includes('-') || queryParams?.['date'])
      ) {

        metaEl.setAttribute(
          'content',
          'nofollow, noindex, max-snippet:-1, max-video-preview:-1, max-image-preview:large'
        );
      } else {
        if(!this.isShab && !this.isMashhad){
          
        metaEl.setAttribute(
          'content',
          'follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large'
        );
        }
        else{
          metaEl.setAttribute(
            'content',
            'nofollow, noindex, max-snippet:-1, max-video-preview:-1, max-image-preview:large'
          );
        }
      }
    } else {
      if (!this.isShab && !this.isMashhad) {
      metaEl.setAttribute('name', 'robots');
      metaEl.setAttribute(
        'content',
        'follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large'
      );
      }
      else {
        metaEl.setAttribute('name', 'robots');
        metaEl.setAttribute(
          'content',
          'nofollow, noindex, max-snippet:-1, max-video-preview:-1, max-image-preview:large'
        );
      }
    }

    // linkCanonical.setAttribute('href',this.document.location.origin +  this.router.url.split('?')[0]);
    if (this.document.location.pathname.includes('/home')) {
      linkCanonical.setAttribute('href', 'https://www.iranhotelonline.com');
    } else {
      if (flag)
        linkCanonical.setAttribute(
          'href',
          'https://www.iranhotelonline.com' +
          decodeURIComponent(this.document.location.pathname).toLocaleLowerCase() +
          '?page=' +
          queryParams['page']
        );
      else
        linkCanonical.setAttribute(
          'href',
          'https://www.iranhotelonline.com' +
          decodeURIComponent(this.document.location.pathname).toLocaleLowerCase()
        );
    }

    // this.document.head.appendChild(linkCanonical)
    // this.document.head.appendChild(metaEl)
    this.document.head.insertBefore(metaEl, this.document.head.firstElementChild);
    this.document.head.insertBefore(linkCanonical, this.document.head.firstElementChild);
  }


  private createCanonical(url: any, next: HttpHandler, req: any, event: HttpEvent<any>) {
    let pageIndex = this.route.snapshot.queryParams;
    let flag = false;
    if (url.includes('/search/filter?') || url.includes('/search/dynamicSearchFilter?')) {
     
      if (event instanceof HttpResponse) {
        let response = event.body;
        if (!response.Cards?.length) {
          flag = false;
        }
        if (response.Error) {
          flag = false;
        }
        if (response.Cards?.length) {
          if (
            pageIndex?.['page'] &&
            pageIndex['page'] != '1' &&
            pageIndex['page'] != '0' &&
            !pageIndex['page'].includes('-')
          ) {
            flag = true;
          } else {
            flag = false;
          }
        }

        this.setCanonical(flag);
      }
    } else {
      this.setCanonical(false);
    }
  }

  createNextAndPrevLinks(params: string[], hasQueryParams: boolean, response: SearchResponse) {
    const foundedPage = params
      .filter((each) => each.split('=')[0] === 'page' || each.split('=')[0] === '?page')
      .join('');
    const currentPageNumber = foundedPage.split('=')?.[1]
      ? Number(foundedPage.split('=')[1])
      : undefined;
    const foundIndex = params.indexOf(foundedPage);
    const isLastPage = Math.ceil(response?.PageTitle?.CountHotel / Number(currentPageNumber)) <= 10; //10 is our pageSize
    if (response?.Cards.length <= 10 && !isLastPage) return;
    let nextPageUrl = '';
    let prevPageUrl = '';
    if (currentPageNumber && currentPageNumber > 1 && foundIndex !== -1) {
      params.splice(foundIndex, 1); // now we have all params but page
      const queryParamsWithoutPage = params.join('&');
      const prevPageLink = this.document.createElement('link');
      nextPageUrl =
        'https://www.iranhotelonline.com' +
        this.document.location.pathname +
        queryParamsWithoutPage +
        (hasQueryParams ? '&' : '?') +
        `page=${currentPageNumber + 1}`;
      prevPageUrl =
        'https://www.iranhotelonline.com' +
        this.document.location.pathname +
        queryParamsWithoutPage +
        (currentPageNumber === 2 ? '' : (hasQueryParams ? '&' : '?') + `page=${currentPageNumber - 1}`);

      if (!isLastPage) {
        const nextPageLink = this.document.createElement('link');
        nextPageLink.setAttribute('rel', 'next');
        nextPageLink.setAttribute('href', nextPageUrl);
        this.document.head.appendChild(nextPageLink);
      }

      prevPageLink.setAttribute('rel', 'prev');
      prevPageLink.setAttribute('href', prevPageUrl);
      this.document.head.appendChild(prevPageLink);
    } else {
      //----------- is first page
      const nextPageLink = this.document.createElement('link');
      nextPageUrl =
        'https://www.iranhotelonline.com' +
        this.document.location.pathname +
        (hasQueryParams ? this.document.location.search + '&page=2' : '?page=2');
      nextPageLink.setAttribute('rel', 'next');
      nextPageLink.setAttribute('href', nextPageUrl);
      this.document.head.appendChild(nextPageLink);
    }
    this.createPaginationButtonsForJsDisabled(nextPageUrl, prevPageUrl);
  }

  createPaginationButtonsForJsDisabled(nextPageLink: string, prevPageLink: string) {
    
    const contentWrapper = this.document.querySelector('#cardsWrapper');
    if (!contentWrapper) return;

    const classes =
      'px-4 py-2 text-base text-white bg-blue-600 flex items-center justify-center seo-friendly-pagination-buttons';
    const nextBtnElement = this.document.createElement('a');
    const prevBtnElement = this.document.createElement('a');

    prevBtnElement.setAttribute('target', '_blank');
    if (prevPageLink) {
      prevBtnElement.setAttribute('href', prevPageLink);
      nextBtnElement.style.position = 'absolute';
      nextBtnElement.style.clip = 'rect(0,0,0,0)';
      nextBtnElement.style.pointerEvents = 'none';
      prevBtnElement.textContent = 'صفحه قبلی';
    }
    // prevBtnElement.classList.add(classes)

    nextBtnElement.setAttribute('target', '_blank');
    if (nextPageLink) {
      nextBtnElement.setAttribute('href', nextPageLink);
      nextBtnElement.style.position = 'absolute';
      nextBtnElement.style.clip = 'rect(0,0,0,0)';
      nextBtnElement.style.pointerEvents = 'none';
      nextBtnElement.textContent = 'صفحه بعدی';
    }
    // nextBtnElement.classList.add(classes)

    if (!nextPageLink) {
      // is last page
      nextBtnElement.setAttribute('disabled', 'true');
    } else if (!prevPageLink) {
      // is first page
      prevBtnElement.setAttribute('disabled', 'true');
    }

    this.document.body.appendChild(prevBtnElement);
    this.document.body.appendChild(nextBtnElement);
  }

  hidePaginationButtons() {
    const buttons = this.document.querySelectorAll('.seo-friendly-pagination-buttons');
    if (!buttons) return;

    buttons.forEach((el: Element, key: number, _: any) => {
      el.classList.add('!hidden');
    });
  }
}
